// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.

import './styles.css';
import type { DSNSnapshot } from './types';
import { fetchSnapshot } from './api';
import { createSession, updateSession } from './state';
import type { SessionState } from './state';
import { renderNow } from './views/now';
import {
  renderSpacecraftDirectory,
  renderSpacecraftDetail,
  setCategory,
} from './views/spacecraft';
import { mountMapOverlay, renderMap, destroyMap } from './views/map';
import { renderFlow } from './views/flow';
import { renderMatrix } from './views/matrix';
import { renderInsights } from './views/insights';
import { GLOSSARY } from './glossary';
import { formatClockUTC, formatRelative } from './utils/format';
import { initTooltip } from './utils/tooltip';

type ViewName = 'now' | 'spacecraft' | 'map' | 'flow' | 'matrix' | 'insights';

const REFRESH_MS = 10_000;

interface AppState {
  snapshot: DSNSnapshot | null;
  session: SessionState;
  activeView: ViewName;
  lastUpdated: number;
  lastError: string | null;
  spacecraftModalOpen: string | null;
  aboutOpen: boolean;
}

const state: AppState = {
  snapshot: null,
  session: createSession(),
  activeView: (localStorage.getItem('dsnwatch.view') as ViewName | null) ?? 'now',
  lastUpdated: 0,
  lastError: null,
  spacecraftModalOpen: null,
  aboutOpen: false,
};

function appShell(): string {
  return `
    <header class="site-header">
      <a class="brand" href="/" style="text-decoration: none; color: inherit;">
        <svg class="brand-icon" viewBox="0 0 64 64" aria-hidden="true">
          <circle cx="32" cy="36" r="14" stroke="#f5a623" stroke-width="2.5" fill="none"/>
          <circle cx="32" cy="36" r="3" fill="#f5a623"/>
          <path d="M32 36 L32 10" stroke="#4cc9f0" stroke-width="2.5" stroke-linecap="round"/>
          <circle cx="32" cy="10" r="3" fill="#4cc9f0"/>
          <path d="M20 52 L44 52" stroke="#f5a623" stroke-width="2.5" stroke-linecap="round"/>
        </svg>
        <div>
          <div class="brand-title">DSN Watch</div>
          <div class="brand-sub">Deep Space Network · Live</div>
        </div>
      </a>
      <nav class="tabs" role="tablist" aria-label="Views">
        <button class="tab-btn ${state.activeView === 'now' ? 'active' : ''}" data-view="now" role="tab">Now</button>
        <button class="tab-btn ${state.activeView === 'spacecraft' ? 'active' : ''}" data-view="spacecraft" role="tab">Spacecraft</button>
        <button class="tab-btn ${state.activeView === 'map' ? 'active' : ''}" data-view="map" role="tab">Map</button>
        <button class="tab-btn ${state.activeView === 'flow' ? 'active' : ''}" data-view="flow" role="tab">Flow</button>
        <button class="tab-btn ${state.activeView === 'matrix' ? 'active' : ''}" data-view="matrix" role="tab">Matrix</button>
        <button class="tab-btn ${state.activeView === 'insights' ? 'active' : ''}" data-view="insights" role="tab">Insights</button>
      </nav>
      <div class="header-meta">
        <span class="clock" id="clock" aria-label="Current UTC time"></span>
        <span class="refresh-indicator" id="refresh-indicator">
          <span class="pulse-dot"></span>
          <span id="refresh-status">Connecting…</span>
        </span>
        <button class="help-btn" id="about-btn" aria-label="About DSN Watch" data-tip="About DSN Watch">?</button>
      </div>
    </header>
    <main class="main-content">
      <div id="view-container"></div>
    </main>
    <footer class="site-footer">
      Data from the public <a href="https://eyes.nasa.gov/dsn/dsn.html" target="_blank" rel="noopener">NASA DSN Now</a> feed ·
      Not affiliated with NASA or JPL · Built by <a href="https://benrichardson.dev/">benrichardson.dev</a> · <a href="https://hub.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>
    </footer>
    <div id="sc-detail-overlay" class="sc-detail-overlay" role="dialog" aria-modal="true">
      <div class="sc-detail-modal" id="sc-detail-modal"></div>
    </div>
    <div id="about-modal" role="dialog" aria-modal="true" aria-labelledby="about-title">
      <div class="modal-content">
        <h2 id="about-title">About DSN Watch</h2>
        <p>NASA's <strong>Deep Space Network</strong> is a global array of giant radio antennas — the 70 m and 34 m dishes scattered across Goldstone (California), Madrid (Spain), and Canberra (Australia) — that communicate with every spacecraft operating beyond low Earth orbit.</p>
        <p>DSN Watch is an independent, consumer-friendly reimagining of NASA's own <a href="https://eyes.nasa.gov/dsn/dsn.html" target="_blank" rel="noopener">DSN Now</a> display. It reads the same public XML feed (served with permissive CORS) and refreshes every ${REFRESH_MS / 1000} seconds.</p>

        <h3>What you're seeing</h3>
        <ul>
          <li><strong>Now</strong> — every antenna, its current target, az/el pointing, wind, signal bands.</li>
          <li><strong>Spacecraft</strong> — curated directory of the missions the DSN tracks, with live-on-sky badges.</li>
          <li><strong>Map</strong> — the three ground complexes on a world map. They're spaced ~120° apart so Earth can always see any distant probe.</li>
          <li><strong>Flow</strong> — which antenna is talking to which spacecraft, coloured by radio band.</li>
          <li><strong>Matrix</strong> — dish × spacecraft grid; great for spotting MSPA (one dish → many spacecraft).</li>
          <li><strong>Insights</strong> — network stats, automatic callouts, and a session-long history of what you've observed.</li>
        </ul>

        <h3>Data source &amp; caveats</h3>
        <p>The feed at <code>eyes.nasa.gov/dsn/data/dsn.xml</code> updates about every 5 seconds. We poll it every 10 seconds to stay well under any reasonable rate limit. Fields like round-trip light time (RTLT) and signal power only appear when NASA's schedulers include them — many tracks will show "—".</p>
        <p>The curated spacecraft directory is maintained by hand; unknown codes show the raw identifier. If you spot a mistake, ping us.</p>

        <h3>Glossary terms</h3>
        <p style="font-size: var(--font-size-sm);">Anything with a dotted underline (like <span class="glossary-link" data-term="mspa">MSPA</span>) is a glossary term — click it for a plain-English explanation.</p>

        <div class="close-row">
          <button class="tab-btn active" id="about-close">Got it</button>
        </div>
      </div>
    </div>
    <div id="glossary-tooltip"></div>
  `;
}

function mount(): void {
  const root = document.getElementById('app');
  if (!root) return;
  root.innerHTML = appShell();
  initTooltip();
  attachHeaderEvents();
  attachModalEvents();
  attachGlossary();
  updateClock();
  setInterval(updateClock, 1000);
  renderActiveView();
  startPolling();
}

function updateClock(): void {
  const el = document.getElementById('clock');
  if (el) el.textContent = formatClockUTC(new Date());
  const refresh = document.getElementById('refresh-status');
  if (refresh && state.lastUpdated > 0 && !state.lastError) {
    refresh.textContent = `Live · updated ${formatRelative(Date.now() - state.lastUpdated)}`;
  }
}

function attachHeaderEvents(): void {
  const tabs = document.querySelectorAll<HTMLButtonElement>('.tab-btn[data-view]');
  tabs.forEach((btn) => {
    btn.addEventListener('click', () => {
      const view = btn.dataset.view as ViewName;
      if (view === state.activeView) return;
      if (state.activeView === 'map') destroyMap();
      state.activeView = view;
      localStorage.setItem('dsnwatch.view', view);
      tabs.forEach((b) => b.classList.toggle('active', b === btn));
      renderActiveView();
    });
  });

  document.getElementById('about-btn')?.addEventListener('click', () => {
    document.getElementById('about-modal')?.classList.add('open');
  });
}

function attachModalEvents(): void {
  document.getElementById('about-close')?.addEventListener('click', () => {
    document.getElementById('about-modal')?.classList.remove('open');
  });
  document.getElementById('about-modal')?.addEventListener('click', (e) => {
    if (e.target === e.currentTarget) {
      (e.currentTarget as HTMLElement).classList.remove('open');
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      document.getElementById('about-modal')?.classList.remove('open');
      document.getElementById('sc-detail-overlay')?.classList.remove('open');
      state.spacecraftModalOpen = null;
      hideGlossaryTooltip();
    }
  });

  document.getElementById('sc-detail-overlay')?.addEventListener('click', (e) => {
    if (e.target === e.currentTarget) {
      (e.currentTarget as HTMLElement).classList.remove('open');
      state.spacecraftModalOpen = null;
    }
  });
}

function attachGlossary(): void {
  const tooltip = document.getElementById('glossary-tooltip');
  if (!tooltip) return;

  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const link = target.closest?.('.glossary-link');
    if (link) {
      e.preventDefault();
      const term = (link as HTMLElement).dataset.term;
      if (term) showGlossaryTooltip(term, link as HTMLElement);
      return;
    }
    if (!target.closest?.('#glossary-tooltip')) {
      hideGlossaryTooltip();
    }
  });
}

function showGlossaryTooltip(term: string, anchor: HTMLElement): void {
  const tooltip = document.getElementById('glossary-tooltip');
  const entry = GLOSSARY[term.toLowerCase()];
  if (!tooltip || !entry) return;
  tooltip.innerHTML = `
    <div class="gt-term">${entry.term}</div>
    <div class="gt-short">${entry.short}</div>
    <div>${entry.long}</div>
  `;
  tooltip.classList.add('open');
  const rect = anchor.getBoundingClientRect();
  const ttRect = tooltip.getBoundingClientRect();
  let left = rect.left;
  let top = rect.bottom + 8;
  if (left + ttRect.width > window.innerWidth - 8) left = window.innerWidth - ttRect.width - 8;
  if (top + ttRect.height > window.innerHeight - 8) top = rect.top - ttRect.height - 8;
  tooltip.style.left = `${Math.max(8, left)}px`;
  tooltip.style.top = `${Math.max(8, top)}px`;
}

function hideGlossaryTooltip(): void {
  document.getElementById('glossary-tooltip')?.classList.remove('open');
}

function renderActiveView(): void {
  const container = document.getElementById('view-container');
  if (!container) return;

  if (!state.snapshot) {
    container.innerHTML = `
      <div class="view-header">
        <h2 class="view-title">Live network status</h2>
        <p class="view-subtitle">Loading the DSN feed…</p>
      </div>
      <div class="loading-grid">
        ${'<div class="loading-skeleton loading-card"></div>'.repeat(3)}
      </div>
    `;
    return;
  }

  const snapshot = state.snapshot;
  switch (state.activeView) {
    case 'now':
      container.innerHTML = renderNow(snapshot);
      attachSpacecraftChips();
      break;
    case 'spacecraft':
      container.innerHTML = renderSpacecraftDirectory(snapshot);
      attachSpacecraftChips();
      attachSpacecraftFilters();
      break;
    case 'map':
      container.innerHTML = mountMapOverlay();
      setTimeout(() => renderMap(snapshot), 0);
      break;
    case 'flow':
      container.innerHTML = renderFlow(snapshot);
      break;
    case 'matrix':
      container.innerHTML = renderMatrix(snapshot);
      break;
    case 'insights':
      container.innerHTML = renderInsights(snapshot, state.session);
      break;
  }

  if (state.lastError) {
    const banner = document.createElement('div');
    banner.className = 'error-banner';
    banner.innerHTML = `
      <span>Live feed offline — showing last known data. ${state.lastError}</span>
      <button id="retry-btn">Retry</button>
    `;
    container.prepend(banner);
    document.getElementById('retry-btn')?.addEventListener('click', () => {
      refresh();
    });
  }
}

function attachSpacecraftChips(): void {
  document.querySelectorAll<HTMLButtonElement>('[data-spacecraft]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const code = btn.dataset.spacecraft;
      if (code) openSpacecraftModal(code);
    });
  });
}

function attachSpacecraftFilters(): void {
  document.querySelectorAll<HTMLButtonElement>('.filter-pill[data-cat]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const cat = btn.dataset.cat as Parameters<typeof setCategory>[0];
      setCategory(cat);
      renderActiveView();
    });
  });
}

function openSpacecraftModal(code: string): void {
  state.spacecraftModalOpen = code;
  const modal = document.getElementById('sc-detail-modal');
  const overlay = document.getElementById('sc-detail-overlay');
  if (!modal || !overlay) return;
  modal.innerHTML = renderSpacecraftDetail(code, state.snapshot);
  overlay.classList.add('open');
  document.getElementById('sc-detail-close')?.addEventListener('click', () => {
    overlay.classList.remove('open');
    state.spacecraftModalOpen = null;
  });
}

let pollingAbort: AbortController | null = null;
let pollingTimer: number | null = null;

async function refresh(): Promise<void> {
  pollingAbort?.abort();
  pollingAbort = new AbortController();
  const indicator = document.getElementById('refresh-indicator');
  const status = document.getElementById('refresh-status');
  const dot = indicator?.querySelector('.pulse-dot');
  dot?.classList.remove('error');
  if (status) status.textContent = 'Refreshing…';
  try {
    const snap = await fetchSnapshot(pollingAbort.signal);
    state.snapshot = snap;
    state.lastUpdated = Date.now();
    state.lastError = null;
    updateSession(state.session, snap);
    if (state.spacecraftModalOpen) {
      const modal = document.getElementById('sc-detail-modal');
      if (modal) modal.innerHTML = renderSpacecraftDetail(state.spacecraftModalOpen, snap);
    }
    renderActiveView();
    if (status) status.textContent = 'Live · updated just now';
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') return;
    state.lastError = err instanceof Error ? err.message : 'Unknown error';
    if (status) status.textContent = 'Feed offline';
    dot?.classList.add('error');
    renderActiveView();
  }
}

function startPolling(): void {
  refresh();
  if (pollingTimer !== null) window.clearInterval(pollingTimer);
  pollingTimer = window.setInterval(refresh, REFRESH_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      if (pollingTimer !== null) {
        window.clearInterval(pollingTimer);
        pollingTimer = null;
      }
    } else if (pollingTimer === null) {
      refresh();
      pollingTimer = window.setInterval(refresh, REFRESH_MS);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mount);
} else {
  mount();
}
